import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlarmClock, Download, FileCheck2, MessageCircleHeart, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, downloadFile, errMsg } from "@/lib/api";

export const ChecklistReminder = ({ r }) => {
  if (!r) return null;
  const pct = Math.round((r.done * 100) / r.total);
  return (
    <Link to={`/app/library?pack=${r.key}`} data-testid="checklist-reminder"
      className="fade-up mb-5 flex flex-wrap items-center justify-between gap-4 rounded-2xl border-2 border-sun/60 bg-sun/10 p-6 hover:bg-sun/20 transition-colors">
      <div className="flex items-start gap-4 max-w-2xl">
        <AlarmClock className="h-6 w-6 text-pinkb shrink-0 mt-1" />
        <div>
          <div className="eyebrow mb-1">{r.days_until} {r.days_until === 1 ? "day" : "days"} to go · {r.name}</div>
          <p className="font-serif text-xl text-navy" data-testid="checklist-reminder-text">You're {pct}% of the way there: {r.done} of {r.total} quick wins ticked. Finish the rest this week.</p>
          <div className="mt-3 h-2 w-full max-w-sm rounded-full bg-white overflow-hidden"><div className="h-full bg-pinkb" style={{ width: `${pct}%` }} /></div>
        </div>
      </div>
      <span className="rounded-full bg-navy text-white px-5 py-2 text-sm">Open my checklist</span>
    </Link>
  );
};

export const PlanDeliveredBanner = () => {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/me/plan-orders").then((r) => setOrders(r.data)).catch(() => {}); }, []);
  const o = orders.find((x) => x.status === "delivered" && x.has_file && !x.banner_dismissed && !x.downloaded_at);
  if (!o) return null;
  const hide = () => setOrders((l) => l.map((x) => (x.id === o.id ? { ...x, banner_dismissed: true } : x)));
  const download = () => downloadFile(`/plan-orders/${o.id}/file`, o.file_name || "mini-marketing-plan.pdf").then(hide).catch((e) => toast.error(errMsg(e)));
  const dismiss = () => { api.post(`/me/plan-orders/${o.id}/dismiss`).catch(() => {}); hide(); };
  return (
    <div className="fade-up mb-5 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-leaf/15 border-2 border-leaf/50 p-6" data-testid="plan-delivered-banner">
      <div className="flex items-start gap-4">
        <FileCheck2 className="h-6 w-6 text-leaf shrink-0 mt-1" />
        <div>
          <div className="eyebrow mb-1">Delivered</div>
          <p className="font-serif text-xl text-navy">Your Mini Marketing Plan is ready. Lindsay has uploaded your PDF.</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={download} data-testid="plan-delivered-download" className="flex items-center gap-2 rounded-full bg-pinkb hover:bg-pinkb/90 text-white px-5 py-2 text-sm transition-colors"><Download className="h-4 w-4" />Download my plan</button>
        <button onClick={dismiss} data-testid="plan-delivered-dismiss" aria-label="Dismiss" className="p-2 text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

export const BrandDocBanner = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/me/brand-doc").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d?.eligible || !["none", "failed"].includes(d.status)) return null;
  return (
    <Link to="/app/brand-foundations" data-testid="dashboard-brand-doc-banner" className="fade-up mb-5 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-navy text-white p-6 hover:bg-navy/95 transition-colors">
      <div className="max-w-2xl">
        <div className="eyebrow !text-sun mb-1">Pro onboarding · about 15 minutes</div>
        <p className="font-serif text-xl">Answer a few questions and get your Brand Foundations Document, ready to paste into any AI tool.</p>
      </div>
      <span className="rounded-full bg-pinkb px-5 py-2 text-sm">Start now</span>
    </Link>
  );
};

export const BetaQuestionCard = () => {
  const [q, setQ] = useState(null);
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  useEffect(() => { api.get("/me/beta-question").then((r) => setQ(r.data)).catch(() => {}); }, []);
  if (!q?.active || (q.answered && !sent) || localStorage.getItem(`beta-q-${q.week}`)) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/me/beta-question", { answer }); setSent(true); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const later = () => { localStorage.setItem(`beta-q-${q.week}`, "1"); setQ({ ...q, active: false }); };
  return (
    <div className="fade-up mb-5 rounded-2xl border-2 border-sun bg-sun/10 p-6" data-testid="beta-question-card">
      <div className="eyebrow mb-1 flex items-center gap-2"><MessageCircleHeart className="h-3.5 w-3.5" />Beta question of the week</div>
      {sent ? <p className="font-serif text-xl text-navy" data-testid="beta-question-thanks">Thank you. Lindsay reads every answer, and it shapes what we fix before launch.</p> : (
        <form onSubmit={submit}>
          <p className="font-serif text-xl text-navy" data-testid="beta-question-text">{q.question}</p>
          <Textarea required rows={2} value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="A sentence is plenty" data-testid="beta-question-answer" className="mt-3 bg-white" />
          <div className="mt-3 flex items-center gap-3">
            <Button type="submit" disabled={busy || !answer.trim()} data-testid="beta-question-submit" className="rounded-full bg-navy hover:bg-navy/90 text-white">Send</Button>
            <button type="button" onClick={later} data-testid="beta-question-later" className="text-sm text-muted-foreground hover:text-navy">Not this week</button>
          </div>
        </form>
      )}
    </div>
  );
};
